import React, { useState, KeyboardEvent, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AddItemInputProps {
  placeholder: string;
  onAdd: (value: string) => void;
  icon?: React.ReactNode; // Optional icon for the button label or input prefix
  useLargeButton?: boolean; // Determines button style/size
  existingItems?: string[]; // Optional: To prevent adding duplicates
  showEnterHint?: boolean; // When true, shows a small hint while typing
}

export const AddItemInput: React.FC<AddItemInputProps> = ({
  placeholder,
  onAdd,
  icon, // Icon not directly used in button, passed for potential parent use
  useLargeButton = true, // Default to the larger button style
  existingItems = [],
  showEnterHint = false,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleAddItem = () => {
    const value = inputValue.trim();
    if (value && !existingItems.includes(value)) {
      onAdd(value);
      setInputValue(''); // Clear input after adding
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission if applicable
      handleAddItem();
    }
  };

  return (
    <div className="w-full">
      <div className={`flex ${useLargeButton ? 'gap-2 items-start' : 'gap-1 items-center'}`}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={handleAddItem}
          aria-label={t('common.add', 'Add')}
          className={`flex items-center justify-center rounded-lg text-white transition-colors
                      ${useLargeButton 
                        ? 'p-2 bg-primary-600 hover:bg-primary-700 mt-0'
                        : 'p-1.5 bg-primary-600 hover:bg-primary-700'
                      }`}
        >
          <Plus className={`${useLargeButton ? 'w-5 h-5' : 'w-4 h-4'}`} />
        </button>
      </div>
      {showEnterHint && (isFocused || inputValue.length > 0) && (
        <div className="mt-1 text-xs text-gray-500">
          {t('addItemInput.hint.prefix', 'Remember to tap')}{' '}<strong>Enter</strong>{' '}
          {t('addItemInput.hint.suffix', 'when you finish in order to add it below')}
        </div>
      )}
    </div>
  );
}; 